/**
 * Ladder shape and palette for the VPN debugger.
 *
 * STEP_ORDER / STEP_TITLES duplicate the server's `diagnosis.STEP_ORDER` on
 * purpose: the UI draws every step before the first response lands, so it needs
 * the list without having asked for it. If a step is added server-side it must
 * be added here too, or it renders under its raw id.
 *
 * The status palette is the same one `getStatusStyles` uses in
 * WireGuardTunnelsTable, so green means the same thing on both tabs.
 */
import {
  VpnDebugClassificationCode,
  VpnDebugFaultOwner,
  VpnDebugRenderStatus,
  VpnDebugStepId,
} from "./vpnDebugInterfaces";

export const POLL_INTERVAL_MS = 3000;
/** Wall-clock cap per plane. The device plane alone can legitimately take ~2 min. */
export const MAX_WAIT_MS = 10 * 60 * 1000;

export const STEP_ORDER: VpnDebugStepId[] = [
  "tunnel_config",
  "tunnel_status",
  "tunnel_flow",
  "tunnel_reachability",
  "service_reachability",
  "authentication",
  "stream_validation",
  "media_inspection",
  "snapshot",
];

/** The steps plane A answers; the rest belong to plane B.
 *
 * `tunnel_flow` is in neither: it is a separate, operator-triggered 30s window
 * (`network-vpn-debug/flow/`), so it stays "skipped" until someone asks for it.
 */
export const TUNNEL_PLANE_STEPS: VpnDebugStepId[] = [
  "tunnel_config",
  "tunnel_status",
  "tunnel_reachability",
];

/** Reason codes on `tunnel_status` where watching the wire is worth 30s.
 *
 * Mirrors STALE_HANDSHAKE_REASONS in admin's console. On these the next
 * question is always "are their packets reaching us at all?", which only the
 * arrival counters can answer.
 */
export const STALE_HANDSHAKE_REASONS: string[] = [
  "tunnel_status.stale_handshake_gateway_healthy",
  "tunnel_status.stale_handshake_gateway_quiet",
];

/** Default length of the packet-flow window, in seconds. */
export const FLOW_WINDOW_SECONDS = 30;

export const STEP_TITLES: Record<VpnDebugStepId, string> = {
  tunnel_config: "Tunnel configuration",
  tunnel_status: "Tunnel status",
  tunnel_flow: "Packet flow",
  tunnel_reachability: "Tunnel reachability (ICMP)",
  service_reachability: "Service reachability (TCP)",
  authentication: "Authentication",
  stream_validation: "Stream validation",
  media_inspection: "Media inspection",
  snapshot: "Snapshot",
};

export interface StatusStyle {
  backgroundColor: string;
  color: string;
  /** Empty means draw a hollow ring instead of a filled dot. */
  dotColor: string;
  label: string;
}

export const STATUS_STYLES: Record<VpnDebugRenderStatus, StatusStyle> = {
  pass: {
    backgroundColor: "#e6f4ea",
    color: "#1e7d34",
    dotColor: "#1e7d34",
    label: "Pass",
  },
  fail: {
    backgroundColor: "#fdecec",
    color: "#b3261e",
    dotColor: "#b3261e",
    label: "Fail",
  },
  warn: {
    backgroundColor: "#fff4e5",
    color: "#b25e00",
    dotColor: "#b25e00",
    label: "Warning",
  },
  skipped: {
    backgroundColor: "#eceff3",
    color: "#5f6368",
    dotColor: "",
    label: "Skipped",
  },
  running: {
    backgroundColor: "#eaf1fb",
    color: "#283e6d",
    dotColor: "#283e6d",
    label: "Running",
  },
  pending: {
    backgroundColor: "#f5f6f8",
    color: "#9aa0a6",
    dotColor: "",
    label: "Waiting",
  },
};

export const CLASSIFICATION_STYLES: Record<
  VpnDebugClassificationCode,
  { backgroundColor: string; accent: string }
> = {
  healthy: { backgroundColor: "#e6f4ea", accent: "#1e7d34" },
  tunnel_down: { backgroundColor: "#fdecec", accent: "#b3261e" },
  routing_firewall: { backgroundColor: "#fdecec", accent: "#b3261e" },
  service_unreachable: { backgroundColor: "#fff4e5", accent: "#b25e00" },
  auth_failure: { backgroundColor: "#fff4e5", accent: "#b25e00" },
  stream_encoding: { backgroundColor: "#fff4e5", accent: "#b25e00" },
  inconclusive: { backgroundColor: "#eceff3", accent: "#5f6368" },
};

/**
 * "unknown" is absent on purpose — printing a label for an unattributed fault
 * adds a word and no information, so the line shows confidence alone.
 */
export const OWNER_LABELS: Partial<Record<VpnDebugFaultOwner, string>> = {
  customer: "Customer side",
  actuate: "Our side",
  device: "Device side",
};

export const DEFAULT_RTSP_PORT = "554";
export const DEFAULT_ONVIF_PORT = "80";

/** Shared with the Tunnels tab's panels so the two tabs read as one page. */
export const PANEL_BORDER = "1px solid #d1d5db";
export const TARGET_PANEL_WIDTH_PX = 360;

export const MULISH = { fontFamily: "Mulish" } as const;
export const SECTION_LABEL_SX = {
  fontFamily: "Mulish",
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "0.6px",
  color: "#6b7280",
  textTransform: "uppercase",
} as const;
export const FIELD_LABEL_SX = {
  fontFamily: "Mulish",
  fontWeight: 700,
  fontSize: 13,
  color: "#001943",
} as const;
export const MONO = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

/**
 * Warn when the server sends a step this build does not know about.
 *
 * The ladder grew twice server-side (`tunnel_config`, `tunnel_flow`) while this
 * UI stayed on seven steps, and the only symptom was a step rendering under its
 * raw id -- easy to miss in a list of nine. Drift is now noisy instead of
 * silent. Deliberately a warning, not a throw: a newer server must never break
 * an older UI, it should just be visible that it happened.
 */
const warnedStepIds = new Set<string>();

export const assertKnownSteps = (ids: string[] | undefined | null): void => {
  (ids || []).forEach((id) => {
    if (STEP_ORDER.includes(id as VpnDebugStepId) || warnedStepIds.has(id)) {
      return;
    }
    warnedStepIds.add(id);
    // eslint-disable-next-line no-console
    console.warn(
      `[vpn-debugger] server sent unknown step "${id}" -- this UI is behind ` +
        "the server ladder; add it to STEP_ORDER/STEP_TITLES/VpnDebugStepId."
    );
  });
};
